import React, { useState, useEffect } from 'react';
import {
  ArrowRight, Mail, Phone, MessageCircle, Bot, Database, Shield,
  Lightbulb, GraduationCap, Settings, Scale, Stethoscope,
  TrendingUp, Users, Factory, Headphones, Check, Menu, X,
  Zap, Lock, Globe, Award, Target, Sparkles, Building2, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ContactForm from '../components/ContactForm';
import Analytics from '../utils/analytics';
import {
  mission, agenticSpecialization, capabilities, services, preBuiltRAGApps, useCases, companyInfo, contactInfo
} from '../data/agenticMock';

const iconMap = {
  Bot, Database, Shield, Lightbulb, GraduationCap, Settings,
  Scale, Stethoscope, TrendingUp, Users, Factory, Headphones,
  Check, Zap, Lock, Globe, Award, Target, Sparkles, Building2,
  Eye, MessageCircle
};

export const Landing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = ['home', 'services', 'apps', 'usecases', 'pricing', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleContact = (type) => {
    let contactValue = '';
    switch(type) {
      case 'email':
        contactValue = contactInfo.email;
        window.location.href = `mailto:${contactInfo.email}`;
        break;
      case 'phone':
        contactValue = contactInfo.phone;
        window.location.href = `tel:${contactInfo.phone}`;
        break;
      case 'whatsapp':
        contactValue = contactInfo.whatsapp;
        window.open(`https://wa.me/${contactInfo.whatsapp}`, '_blank');
        break;
      default:
        break;
    }
    Analytics.trackContactClick(type, contactValue);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0a0e14' }}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-[#00FFB2]/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
              <img 
                src="/mg-ai-logo-new.png" 
                alt="MG-AI - Agentic AI and RAG Solutions Provider" 
                className="h-10 w-auto"
                width="120"
                height="40"
              />
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              {[
                { id: 'home', label: 'Home' },
                { id: 'services', label: 'Solutions' },
                { id: 'apps', label: 'Applications' },
                { id: 'usecases', label: 'Use Cases' },
                { id: 'contact', label: 'Contact' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === item.id ? 'text-[#00FFB2]' : 'text-gray-400 hover:text-[#00FFB2]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#00FFB2] hover:bg-[#00D9AA] text-black font-semibold shadow-lg shadow-[#00FFB2]/30"
              >
                Get Started
              </Button>
            </nav>

            <button className="md:hidden text-[#00FFB2]" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <nav className="md:hidden py-4 space-y-4 border-t border-[#00FFB2]/20">
              {['home', 'services', 'apps', 'usecases', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left text-gray-400 hover:text-[#00FFB2] capitalize py-2"
                >
                  {item === 'apps' ? 'Applications' : item === 'usecases' ? 'Use Cases' : item === 'services' ? 'Solutions' : item}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(#00FFB2 1px, transparent 1px), linear-gradient(90deg, #00FFB2 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#00FFB2]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#00D9FF]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-[#00FFB2]/20 text-[#00FFB2] hover:bg-[#00FFB2]/30 border border-[#00FFB2]/50">
            {companyInfo.tagline}
          </Badge>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            AI Solutions
            <span className="block gradient-text electric-glow mt-2">
              For Any Problem
            </span>
          </h1>

          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            From basic algorithms to advanced robotics. Sales prediction to autonomous systems. Image classification to decision-making AI. Perfect solutions with MG-AI—no problem too complex.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              size="lg"
              onClick={() => {
                scrollToSection('services');
                Analytics.trackButtonClick('Explore Solutions', 'hero');
              }}
              className="bg-[#00FFB2] hover:bg-[#00D9AA] text-black px-8 py-6 text-lg group shadow-lg shadow-[#00FFB2]/40 font-semibold"
            >
              Explore Our Solutions
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                scrollToSection('contact');
                Analytics.trackButtonClick('Book Consultation', 'hero');
              }}
              className="border-2 border-[#00FFB2] text-[#00FFB2] hover:bg-[#00FFB2]/10 px-8 py-6 text-lg font-semibold"
            >
              Get Your Perfect Solution
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {companyInfo.stats.map((stat, index) => (
              <div key={index} className="text-center p-4 rounded-lg bg-white/5 border border-[#00FFB2]/20">
                <div className="text-3xl font-bold text-[#00FFB2] mb-1">{stat.label}</div>
                <div className="text-sm text-gray-400">{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#00FFB2]/5 to-[#00D9FF]/5 border-y border-[#00FFB2]/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              {capabilities.subtitle}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {capabilities.title}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {capabilities.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.areas.map((area, index) => {
              const IconComponent = iconMap[area.icon];
              return (
                <Card
                  key={index}
                  className="bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#00FFB2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#00FFB2]" size={24} />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white mb-2">
                          {area.title}
                        </CardTitle>
                        <CardDescription className="text-gray-300 text-base leading-relaxed">
                          {area.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-[#00FFB2]/10 border border-[#00FFB2]/30 rounded-lg p-6 max-w-3xl">
              <p className="text-gray-300 text-lg leading-relaxed">
                <span className="text-[#00FFB2] font-semibold">No challenge too complex.</span> From basic algorithms to autonomous systems that navigate the physical world—we've mastered it all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Agentic AI & RAG Specialization Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFE500]/5 to-[#00FFB2]/5 border-y border-[#00FFB2]/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#FFE500]/20 text-[#FFE500] border border-[#FFE500]/50">
              {agenticSpecialization.subtitle}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {agenticSpecialization.title}
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {agenticSpecialization.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {agenticSpecialization.highlights.map((highlight, index) => {
              const IconComponent = iconMap[highlight.icon];
              return (
                <Card
                  key={index}
                  className="bg-white/5 border-[#00FFB2]/30 hover:border-[#00FFB2]/60 transition-all"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#00FFB2]/20 to-[#FFE500]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#00FFB2]" size={28} />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white mb-2">
                          {highlight.title}
                        </CardTitle>
                        <CardDescription className="text-gray-300 text-base leading-relaxed">
                          {highlight.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          {/* Governance Framework */}
          <Card className="bg-gradient-to-br from-[#00FFB2]/10 to-[#00D9FF]/10 border-[#00FFB2]/30 max-w-5xl mx-auto">
            <CardHeader>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00FFB2]/20 rounded-full mb-4">
                  <Shield className="text-[#00FFB2]" size={32} />
                </div>
                <CardTitle className="text-2xl text-white mb-3">
                  {agenticSpecialization.governance.title}
                </CardTitle>
                <CardDescription className="text-gray-300 text-lg">
                  Every component of your AI agents is governed, monitored, and compliant
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agenticSpecialization.governance.points.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white/5 p-4 rounded-lg border border-[#00FFB2]/20">
                    <Check className="text-[#00FFB2] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-300 text-sm leading-relaxed">{point}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-white/5 rounded-lg border border-[#FFE500]/30">
                <p className="text-gray-300 text-center leading-relaxed">
                  <span className="text-[#FFE500] font-semibold">Bottom line:</span> We don't just build AI agents—we build the <span className="text-[#00FFB2] font-semibold">most efficient, secure, ethical, and fully governed</span> Agentic AI and RAG systems. From data ingestion to monitoring, every component is transparent, compliant, and under your control.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Tech Ticker */}
      <div className="py-4 bg-black/50 border-y border-[#00FFB2]/20 overflow-hidden">
        <div className="flex animate-scroll whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center space-x-8 mx-4">
              {['LangChain', 'OpenAI', 'Anthropic', 'ChromaDB', 'Pinecone', 'LlamaIndex', 'Weaviate', 'AutoGPT', 'CrewAI'].map((tech) => (
                <span key={tech} className="text-gray-400 font-mono text-sm">{tech}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Mission Section */}
      <section id="mission" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              {mission.subtitle}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {mission.title}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
              {mission.vision}
            </p>
          </div>

          {/* Focus Areas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {mission.focus.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={index}
                  className="bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#00FFB2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#00FFB2]" size={24} />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl text-white mb-3">
                          {item.title}
                        </CardTitle>
                        <ul className="space-y-2">
                          {item.points.map((point, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-300">
                              <Check className="text-[#00FFB2] mr-2 flex-shrink-0 mt-0.5" size={16} />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-black/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              What We Do
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              AI Solutions By Application
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Prediction. Vision. Robotics. Agents. NLP. Detection. We deliver perfect solutions across every AI domain.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="card-glow group bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all duration-300"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-[#00FFB2]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#00FFB2]/30 transition-colors">
                      <IconComponent className="text-[#00FFB2]" size={28} />
                    </div>
                    <CardTitle className="text-xl text-white">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-300">
                          <Check className="text-[#00FFB2] mr-2 flex-shrink-0 mt-0.5" size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Use Cases of RAG Application */}
      <section id="apps" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              Real-World Applications
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Common Use Cases of RAG Application
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Domain-specific AI applications powered by RAG technology, trained on your enterprise data and deployed securely.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {preBuiltRAGApps.map((app) => {
              const IconComponent = iconMap[app.icon];
              return (
                <Card
                  key={app.id}
                  className="card-glow bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-[#00FFB2]/20 rounded-lg flex items-center justify-center">
                        <IconComponent className="text-[#00FFB2]" size={24} />
                      </div>
                      <Badge className="bg-[#FFE500]/20 text-[#FFE500] border-[#FFE500]/50">
                        {app.domain}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg text-white">{app.name}</CardTitle>
                    <CardDescription className="text-gray-400 text-sm">
                      {app.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="text-xs text-gray-500 mb-2">Key Capabilities:</div>
                      <ul className="space-y-1">
                        {app.capabilities.slice(0, 3).map((cap, idx) => (
                          <li key={idx} className="flex items-start text-xs text-gray-300">
                            <Check className="text-[#00FFB2] mr-1 flex-shrink-0 mt-0.5" size={12} />
                            <span>{cap}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-3 border-t border-[#00FFB2]/20">
                      <div className="text-[#00FFB2] font-semibold text-sm">{app.metrics}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="usecases" className="py-20 px-4 sm:px-6 lg:px-8 bg-black/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              Proven Results
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Real Enterprise Use Cases
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              See how leading organizations leverage our Agentic AI and RAG systems for measurable business impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.slice(0, 4).map((useCase) => (
              <Card
                key={useCase.id}
                className="bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-[#00D9FF]/20 text-[#00D9FF] border-[#00D9FF]/50 text-xs">
                      {useCase.industry}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-white">{useCase.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Challenge:</div>
                    <p className="text-sm text-gray-300">{useCase.challenge}</p>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Solution:</div>
                    <p className="text-sm text-gray-300">{useCase.solution}</p>
                  </div>
                  <div className="pt-3 border-t border-[#00FFB2]/20">
                    <div className="text-xs text-gray-500 mb-1">Outcome:</div>
                    <div className="text-[#00FFB2] font-semibold text-sm">{useCase.outcome}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-[#00FFB2] hover:bg-[#00D9AA] text-black font-semibold px-8 py-6 shadow-lg shadow-[#00FFB2]/30"
            >
              Discuss Your Use Case
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action Section - Replacing Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              Get Started
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready For Your Perfect AI Solution?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Whatever your challenge—from simple regression to autonomous robots, from sales prediction to self-driving systems—we deliver perfect execution. Let's discuss your project.
            </p>
          </div>

          <Card className="bg-gradient-to-br from-[#00FFB2]/10 to-[#00D9FF]/10 border-[#00FFB2]/30 max-w-3xl mx-auto">
            <CardContent className="p-12 text-center">
              <div className="mb-8">
                <Sparkles className="text-[#00FFB2] mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-white mb-4">
                  Custom Solutions, Perfect Execution
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">
                  Every AI challenge is unique. We analyze your specific needs, recommend the perfect approach, and deliver a solution that exceeds expectations. No templates, no compromises—just excellence.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <Check className="text-[#00FFB2] mx-auto mb-2" size={24} />
                  <div className="text-sm text-gray-300">Any AI Domain</div>
                </div>
                <div className="text-center">
                  <Check className="text-[#00FFB2] mx-auto mb-2" size={24} />
                  <div className="text-sm text-gray-300">Custom Architecture</div>
                </div>
                <div className="text-center">
                  <Check className="text-[#00FFB2] mx-auto mb-2" size={24} />
                  <div className="text-sm text-gray-300">Perfect Results</div>
                </div>
              </div>

              <Button
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="bg-[#00FFB2] hover:bg-[#00D9AA] text-black px-12 py-6 text-lg font-semibold shadow-lg shadow-[#00FFB2]/40"
              >
                Contact Us For Your Solution
                <ArrowRight className="ml-2" size={20} />
              </Button>

              <p className="text-gray-400 text-sm mt-6">
                Tell us your challenge. We'll craft the perfect AI solution.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              Enterprise-Grade Security
            </Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              Compliance & Data Protection
            </h2>
            <p className="text-xl text-gray-400">
              Your data security is our top priority. Fully compliant with global standards.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {companyInfo.compliance.map((cert, index) => (
              <div
                key={index}
                className="terminal p-6 rounded-lg text-center"
              >
                <Lock className="text-[#00FFB2] mx-auto mb-3" size={32} />
                <div className="text-white font-semibold mb-1">{cert.name}</div>
                <div className="text-[#00FFB2] text-sm">{cert.status}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#00FFB2]/20 text-[#00FFB2] border border-[#00FFB2]/50">
              Let's Connect
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Let's Build Your Perfect Solution
            </h2>
            <p className="text-xl text-gray-400">
              From simple regression to autonomous systems—tell us your challenge and we'll deliver the perfect AI solution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card
              className="group cursor-pointer bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
              onClick={() => handleContact('email')}
            >
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-[#00FFB2]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#00FFB2]/30 transition-colors">
                  <Mail className="text-[#00FFB2]" size={28} />
                </div>
                <CardTitle className="text-xl text-white">Email</CardTitle>
                <CardDescription className="text-gray-400 break-all">
                  {contactInfo.email}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card
              className="group cursor-pointer bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
              onClick={() => handleContact('phone')}
            >
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-[#00D9FF]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#00D9FF]/30 transition-colors">
                  <Phone className="text-[#00D9FF]" size={28} />
                </div>
                <CardTitle className="text-xl text-white">Call</CardTitle>
                <CardDescription className="text-gray-400">
                  {contactInfo.phone}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card
              className="group cursor-pointer bg-white/5 border-[#00FFB2]/20 hover:border-[#00FFB2]/50 transition-all"
              onClick={() => handleContact('whatsapp')}
            >
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-[#FFE500]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#FFE500]/30 transition-colors">
                  <MessageCircle className="text-[#FFE500]" size={28} />
                </div>
                <CardTitle className="text-xl text-white">WhatsApp</CardTitle>
                <CardDescription className="text-gray-400">
                  Chat instantly
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <div className="relative my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#00FFB2]/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0e14] text-gray-500">Or send us a message</span>
            </div>
          </div>

          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-[#00FFB2]/20 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/mg-ai-logo-new.png" 
                  alt="MG-AI Logo - Enterprise AI Solutions"
                  className="h-16 w-auto"
                  width="192"
                  height="64"
                />
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                {companyInfo.mission}
              </p>
              <div className="flex space-x-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleContact('email')}
                  className="border-[#00FFB2] text-[#00FFB2] hover:bg-[#00FFB2]/10"
                >
                  <Mail size={20} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleContact('phone')}
                  className="border-[#00D9FF] text-[#00D9FF] hover:bg-[#00D9FF]/10"
                >
                  <Phone size={20} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleContact('whatsapp')}
                  className="border-[#FFE500] text-[#FFE500] hover:bg-[#FFE500]/10"
                >
                  <MessageCircle size={20} />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4 text-[#00FFB2]">Solutions</h3>
              <ul className="space-y-2">
                {['Agentic AI', 'RAG Systems', 'Training', 'Consulting'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => scrollToSection('services')}
                      className="text-gray-400 hover:text-[#00FFB2] transition-colors text-left text-sm"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4 text-[#00FFB2]">Company</h3>
              <ul className="space-y-2">
                {[
                  { label: 'About', id: 'mission' },
                  { label: 'Use Cases', id: 'usecases' },
                  { label: 'Pricing', id: 'pricing' },
                  { label: 'Contact', id: 'contact' }
                ].map((item) => (
                  <li key={item.label}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-gray-400 hover:text-[#00FFB2] transition-colors text-left text-sm"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-[#00FFB2]/20 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} MG-AI. All rights reserved. Building the future where AI is accessible, safe, and efficient for everyone.</p>
            <div className="mt-2 flex items-center justify-center gap-4 text-xs">
              <span className="text-[#00FFB2]">GDPR Compliant</span>
              <span>•</span>
              <span className="text-[#00FFB2]">SOC 2 Certified</span>
              <span>•</span>
              <span className="text-[#00FFB2]">ISO 27001</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;